/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { CssTools } from './CssTools.js';
import { resolveCssColor } from './ColorTools.js';
import { createTicksFormat } from './TicksFormat.js';
/**
 * Builder to create the value-axis style
 */

export class ValueAxisBuilder {
  constructor() {
    this._title = null;
    this._maxTickCount = null;
    this._tickLength = 0.0;
    this._tickLabelGap = ValueAxisBuilder.AXIS_TICK_LABEL_GAP;
    this._shorthandLabels = null;
    this._decimalPlaces = undefined;
    this._horizontal = null;
    this._fontFamily = null;
    this._axisWidth = ValueAxisBuilder.AXIS_DEFAULT_WIDTH;
  }

  /**
   * The title for the axis
   *
   * @param value - the title for the axis
   */
  set title(value) {
    this._title = value;
  }

  /**
   * The maximum tick count
   *
   * @param value - the maximum tick count
   */
  set maxTickCount(value) {
    this._maxTickCount = value;
  }

  /**
   * The length of a tick in rem
   *
   * @param value - the length of the tick in rem
   * @default 0
   */
  set tickLength(value) {
    this._tickLength = value;
  }

  /**
   * The gap (in rem) between the tip of a tick and the corresponding label
   *
   * @param value - the gap (in rem) between the tip of a tick and the corresponding label
   */
  set tickLabelGap(value) {
    this._tickLabelGap = value;
  }

  /**
   * use abbreviations for big numbers for value-labels (e.g. k=kilo=1000, m=milli=1/1000, etc.)
   */
  set shorthandLabels(value) {
    this._shorthandLabels = value;
  }

  /**
   * The number of decimal places to use. Possible values are from 0 to 10.
   */
  set decimalPlaces(value) {
    this._decimalPlaces = value;
  }

  /**
   * The orientation of the axis
   *
   * @param value - true if the axis is horizontal else false
   */
  set horizontal(value) {
    this._horizontal = value;
  }

  /**
   * The font family to be used for the axis
   *
   * @param family - the font family to be used for the axis
   */
  set fontFamily(family) {
    this._fontFamily = family;
  }

  /**
   * The width of the axis
   *
   * @param width - the width of the axis, set in rem
   */
  set axisWidth(width) {
    this._axisWidth = width;
  }
  /**
   * Ensures that the given axisWidth lies in the range (AXIS_MIN_WIDTH - AXIS_MAX_WIDTH)
   */


  static SANITIZE_AXIS_WIDTH(axisWidth) {
    if (axisWidth > ValueAxisBuilder.AXIS_MAX_WIDTH) {
      // eslint-disable-next-line
      console.warn(`${axisWidth} is bigger than the maximum axis width (= ${ValueAxisBuilder.AXIS_MAX_WIDTH}). The maximum axis width will be used instead`);
      axisWidth = ValueAxisBuilder.AXIS_MAX_WIDTH;
    } else if (axisWidth < ValueAxisBuilder.AXIS_MIN_WIDTH) {
      // eslint-disable-next-line
      console.warn(`${axisWidth} is smaller than the minimum axis width (= ${ValueAxisBuilder.AXIS_MIN_WIDTH}). The minimum axis width will be used instead`);
      axisWidth = ValueAxisBuilder.AXIS_MIN_WIDTH;
    }

    return axisWidth;
  }
  /**
   * Builds a style object for value axes
   */


  build() {
    // check that everything needed is set correctly
    if (this._horizontal === null) {
      throw new Error('no orientation set');
    }

    if (this._fontFamily === null) {
      throw new Error('no font family set');
    }

    if (this._maxTickCount === null) {
      throw new Error('no maximum tick count set');
    }

    if (this._shorthandLabels === null) {
      throw new Error('no shorthand labels set');
    }

    const style = {};
    style.tickFont = {
      family: this._fontFamily,
      size: CssTools.REM2PIXEL(ValueAxisBuilder.TICK_FONT_SIZE),
      style: 'normal',
      weight: 400 // regular

    };
    style.titleFont = {
      family: this._fontFamily,
      size: CssTools.REM2PIXEL(ValueAxisBuilder.TITLE_FONT_SIZE),
      style: 'normal',
      weight: 600 // semi-bold

    };
    style.axisSide = this._horizontal ? 'Bottom' : 'Left';
    style.maxTickCount = this._maxTickCount;
    style.tickLabelGap = CssTools.REM2PIXEL(this._tickLabelGap);
    style.tickLabelColor = resolveCssColor('var(--davinci-color-background-elements-5)');
    style.tickLength = CssTools.REM2PIXEL(this._tickLength);
    style.axisLineColor = resolveCssColor('var(--davinci-color-background-elements-3)');
    style.axisLineWidth = this._horizontal ? ValueAxisBuilder.HORIZONTAL_AXIS_LINE_WIDTH : ValueAxisBuilder.VERTICAL_AXIS_LINE_WIDTH;
    style.title = this._title;
    style.titleGap = this.horizontal ? CssTools.REM2PIXEL(ValueAxisBuilder.HORIZONTAL_AXIS_TITLE_GAP) : CssTools.REM2PIXEL(ValueAxisBuilder.VERTICAL_AXIS_TITLE_GAP);
    style.titleColor = resolveCssColor('var(--davinci-color-background-elements-4)');
    style.axisSize = this._computeAxisSize(CssTools.PIXELS2LINE_HEIGHT(style.titleFont.size));
    const ticksFormat = createTicksFormat(this._shorthandLabels, this._decimalPlaces);
    style.ticksFormat = {
      format(ticksIn, ticksOut, locale) {
        ticksFormat(ticksIn, ticksOut, locale);
      }

    };
    return style;
  }
  /**
   * Computes the height (horizontal) or width (vertical) of this value axis (in pixels).
   *
   * @param titleHeight - the height of the title (in pixels)
   * @returns the size of the value axis (in pixels)
   */


  _computeAxisSize(titleHeight) {
    let axisSize;

    if (this._horizontal) {
      var _this$_title;

      axisSize = CssTools.REM2PIXEL(ValueAxisBuilder.AXIS_DEFAULT_HEIGHT);
      axisSize += ValueAxisBuilder.HORIZONTAL_AXIS_LINE_WIDTH;

      if (((_this$_title = this._title) === null || _this$_title === void 0 ? void 0 : _this$_title.trim().length) > 0) {
        axisSize += CssTools.REM2PIXEL(ValueAxisBuilder.HORIZONTAL_AXIS_TITLE_GAP);
        axisSize += titleHeight;
      }
    } else {
      axisSize = CssTools.REM2PIXEL(this._axisWidth);
      axisSize += ValueAxisBuilder.VERTICAL_AXIS_LINE_WIDTH; // The title that is placed on top does not change the width of a vertical axis.
    }

    return axisSize;
  }

}
ValueAxisBuilder.VERTICAL_AXIS_LINE_WIDTH = 1.0;
ValueAxisBuilder.HORIZONTAL_AXIS_LINE_WIDTH = 0.0;
ValueAxisBuilder.AXIS_DEFAULT_WIDTH = 3.625;
ValueAxisBuilder.AXIS_DEFAULT_HEIGHT = 2.5;
ValueAxisBuilder.AXIS_MAX_WIDTH = 15;
ValueAxisBuilder.AXIS_MIN_WIDTH = 2;
ValueAxisBuilder.TITLE_FONT_SIZE = 0.75;
ValueAxisBuilder.TICK_FONT_SIZE = 0.75;
ValueAxisBuilder.AXIS_TICK_LABEL_GAP = 1.0;
ValueAxisBuilder.HORIZONTAL_AXIS_TITLE_GAP = 0.25;
ValueAxisBuilder.VERTICAL_AXIS_TITLE_GAP = 0.25 + ValueAxisBuilder.TICK_FONT_SIZE * 0.5;
